import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const FRONTEND_URL = typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_FRONTEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      navigate(next && next.startsWith("/") ? next : "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const redirectUri = `${FRONTEND_URL}/auth/google/callback`;
      const response = await axios.get(`${API_URL}/api/auth/google/url`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Failed to initialize Google login");
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-5 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12">
        <Link
          to="/"
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8 sm:mb-12"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="max-w-md w-full">
          <Link to="/" className="inline-block mb-8" data-testid="logo-link">
            <span className="font-['Syne'] text-2xl font-extrabold tracking-tighter text-white">
              DOPE MEDIA
            </span>
          </Link>

          <h1
            className="font-['Syne'] text-4xl md:text-5xl font-bold tracking-tight mb-4 text-white"
            data-testid="login-title"
          >
            WELCOME
            <br />
            BACK
          </h1>
          <p className="text-zinc-400 mb-8">
            Sign in to access your dashboard and manage your promotions.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="form-label text-zinc-400">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="form-input h-12 bg-[#141414] border-[#2A2A2A] text-white placeholder:text-zinc-600 focus:border-[#CCFF00]"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="form-label text-zinc-400">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="form-input h-12 pr-12 bg-[#141414] border-[#2A2A2A] text-white placeholder:text-zinc-600 focus:border-[#CCFF00]"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="toggle-password"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-zinc-500 hover:text-white transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-14 flex items-center justify-center"
              data-testid="login-submit-btn"
            >
              {loading ? <div className="spinner" /> : "Sign In"}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-zinc-200"></div>
            <span className="text-sm text-zinc-500">or</span>
            <div className="flex-1 h-px bg-zinc-200"></div>
          </div>

          {/* Google Login Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-14 flex items-center justify-center gap-3 border-2 border-[#2A2A2A] text-white hover:border-[#CCFF00] bg-transparent"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-login-btn"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-zinc-600 border-t-[#CCFF00] rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Continue with Google
          </Button>

          <p className="text-center mt-8 text-zinc-500">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="font-bold text-[#CCFF00] hover:text-[#B8E600] transition-colors"
              data-testid="register-link"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1760780567530-389d8a3fba75?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Studio"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-['Syne'] text-3xl font-bold leading-tight">
            "DOPE MEDIA changed my career. I went from 1K to 100K monthly listeners."
          </p>
          <p className="mt-4 text-zinc-300">— Rising Artist, 2024</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
